import { forwardRef, useImperativeHandle, useState } from "react";

import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  filterPropertyAlias,
  handlePasteWithoutAccents,
} from "utils/inputFilters";
import { removeAccents } from "utils/utils";
import {
  formatCurrencyDecimal,
  parseCurrencyDecimal,
} from "utils/currencyUtils";

import type { AddressFormData } from "@/types/address";
import { useCreateProperty } from "hooks/useCreateProperty";
import type { PropertyFieldKey } from "../../../utils/propertyValidation";
import { getApiErrorMessage } from "utils/apiErrorMessage";

import { Property as APIProperty } from "js/components/types";

interface CreatePropertyPayload {
  alias: string | null;
  rental_price: string | null;
  maintenance_price: string;
  usage: string | null;
  address: {
    street: string | null;
    exterior_number: string | null;
    interior_number: string | null;
    zip_code: string | null;
    neighborhood: string | null;
    municipality: string | null;
    city: string | null;
    state: string | null;
  };
  landlord_id: string | null;
}
import { AddressForm } from "js/components/Address";
import { Button } from "js/components/ui/button";
import { DialogFooter } from "js/components/ui/dialog";
import { Input } from "js/components/ui/input";
import { Label } from "js/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";
import { Separator } from "js/components/ui/separator";

import { PROPERTY_USE_OPTIONS } from "js/constants/property-use";
import { usePropertyRegistrationForm } from "../hooks/usePropertyRegistrationForm";
import { CancelConfirmationDialog } from "./CancelConfirmationDialog";
import { LandlordSelector } from "./LandlordSelector";

export interface PropertyRegistrationRef {
  handleCancel: () => void;
}

interface PropertyRegistrationProps {
  propertySheetStep: number;
  setPropertySheetStep: (step: number) => void;
  onCancelClick: () => void;
  onPropertyCreated?: (property: APIProperty) => void; // Callback for when property is successfully created
  isVisible?: boolean; // Add this to know when the modal is visible
}

export const PropertyRegistration = forwardRef<
  PropertyRegistrationRef,
  PropertyRegistrationProps
>(
  (
    {
      propertySheetStep,
      setPropertySheetStep,
      onCancelClick,
      onPropertyCreated,
    },
    ref,
  ) => {
    // Use the property registration form hook - must be at top before any references
    const {
      newProperty,
      setNewProperty,
      selectedLandlord,
      setSelectedLandlord,
      resetForm,
      validateStep1,
      hasFormBeenModified,
      getFieldError,
      setFieldTouched,
      validateAllFields,
      touchedFields,
    } = usePropertyRegistrationForm();

    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const createPropertyMutation = useCreateProperty();

    // Convert newProperty address fields to AddressFormData
    const addressFormData: AddressFormData = {
      street: newProperty.street || "",
      exterior_number: newProperty.exterior_number || "",
      interior_number: newProperty.interior_number || "",
      neighborhood: newProperty.colonia || "",
      city: newProperty.city || "",
      municipality: newProperty.municipality || "",
      state: newProperty.state || "",
      zip_code: newProperty.zip_code || "",
    };

    // Handle address changes from AddressForm
    const handleAddressChange = (data: AddressFormData) => {
      setNewProperty((s) => ({
        ...s,
        street: data.street,
        exterior_number: data.exterior_number,
        interior_number: data.interior_number,
        colonia: data.neighborhood,
        municipality: data.municipality,
        city: data.city,
        state: data.state,
        zip_code: data.zip_code,
        direccion: `${data.street} ${data.exterior_number}`.trim(),
      }));
    };

    const addressErrors = {
      street: getFieldError("street"),
      exterior_number: getFieldError("exterior_number"),
      interior_number: getFieldError("interior_number"),
      neighborhood: getFieldError("colonia"),
      city: getFieldError("city"),
      municipality: undefined,
      state: getFieldError("state"),
      zip_code: getFieldError("zip_code"),
    };

    const addressTouched = {
      street: touchedFields.has("street"),
      exterior_number: touchedFields.has("exterior_number"),
      interior_number: touchedFields.has("interior_number"),
      neighborhood: touchedFields.has("colonia"),
      city: touchedFields.has("city"),
      municipality: false,
      state: touchedFields.has("state"),
      zip_code: touchedFields.has("zip_code"),
    };

    const ADDRESS_FIELD_MAP: Partial<Record<keyof AddressFormData, PropertyFieldKey>> = {
      street: "street",
      exterior_number: "exterior_number",
      interior_number: "interior_number",
      neighborhood: "colonia",
      city: "city",
      state: "state",
      zip_code: "zip_code",
    };

    const handleAddressBlur = (field: keyof AddressFormData) => {
      const propertyField = ADDRESS_FIELD_MAP[field];
      if (propertyField) {
        setFieldTouched(propertyField);
      }
    };

    // Transform function matching existing filterStreetInput behavior
    const transformValue = (value: string) =>
      value
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

    // Handle cancel button click
    const handleCancelClick = () => {
      if (hasFormBeenModified()) {
        setShowCancelConfirmation(true);
      } else {
        onCancelClick();
      }
    };

    // Handle back button click from step 2
    const handleBackClick = () => {
      // On step 2, going back doesn't lose data, just navigate back
      setPropertySheetStep(1);
    };

    // Handle confirmation of cancel
    const handleConfirmCancel = () => {
      setShowCancelConfirmation(false);

      // Reset form using the hook's reset function
      resetForm();

      // Reset to step 1 if we're on step 2
      if (propertySheetStep === 2) {
        setPropertySheetStep(1);
      }

      onCancelClick();
    };

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        handleCancel: handleCancelClick,
      }),
      [handleCancelClick],
    );

    // Format number as currency (Mexican Peso), decimal-aware for
    // rent/maintenance (accepts up to 2 decimal places).
    const formatCurrency = (value: string | null) =>
      formatCurrencyDecimal(value ?? "");

    const handleAmountChange = (field: string, value: string) => {
      const numericValue = parseCurrencyDecimal(value);
      setNewProperty((s) => ({ ...s, [field]: numericValue }));
    };

    const renderPropertyForm = () => (
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="md:w-1/4">
            <Label className="text-sm font-medium text-gray-700 mb-1 block">
              Uso que le dará al inmueble *
            </Label>
            <Select
              value={newProperty.uso}
              onValueChange={(v) => {
                setNewProperty((s) => ({ ...s, uso: v }));
                setFieldTouched("uso");
              }}
            >
              <SelectTrigger
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 w-full ${
                  getFieldError("uso") ? "border-red-500" : ""
                }`}
              >
                <SelectValue placeholder="Uso del inmueble" />
              </SelectTrigger>
              <SelectContent>
                {PROPERTY_USE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {getFieldError("uso") && (
              <p className="text-sm text-red-600 mt-1">
                {getFieldError("uso")}
              </p>
            )}
          </div>
          <div className="md:w-3/4">
            <Label className="text-sm font-medium text-gray-700 mb-1 block">
              Alias del inmueble
            </Label>
            <Input
              value={newProperty.alias}
              onChange={(e) => {
                const filtered = filterPropertyAlias(e.target.value);
                setNewProperty((s) => ({
                  ...s,
                  alias: filtered,
                  label: filtered,
                }));
              }}
              onPaste={(e) => {
                handlePasteWithoutAccents(e, filterPropertyAlias, (value) => {
                  setNewProperty((s) => ({
                    ...s,
                    alias: value,
                    label: value,
                  }));
                });
              }}
              onBlur={() => setFieldTouched("alias")}
              placeholder="Alias del inmueble"
              className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 ${
                getFieldError("alias") ? "border-red-500" : ""
              }`}
              maxLength={50}
            />
            {getFieldError("alias") && (
              <p className="text-sm text-red-600 mt-1">
                {getFieldError("alias")}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">
              Valor mensual de la renta *
            </Label>
            <div className="relative">
              <Input
                type="text"
                placeholder="$12,000.00"
                value={formatCurrency(newProperty.rental_price)}
                onChange={(e) =>
                  handleAmountChange("rental_price", e.target.value)
                }
                onBlur={() => setFieldTouched("rental_price")}
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 pr-12 ${
                  getFieldError("rental_price") ? "border-red-500" : ""
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-500 text-sm font-medium">MXN</span>
              </div>
            </div>
            {getFieldError("rental_price") && (
              <p className="text-sm text-red-600 mt-1">
                {getFieldError("rental_price")}
              </p>
            )}
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1 block">
              Valor mensual del mantenimiento
            </Label>
            <div className="relative">
              <Input
                type="text"
                placeholder="$3,000.00"
                value={formatCurrency(newProperty.maintenance_price)}
                onChange={(e) =>
                  handleAmountChange("maintenance_price", e.target.value)
                }
                onBlur={() => setFieldTouched("maintenance_price")}
                className={`border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900 pr-12 ${
                  getFieldError("maintenance_price") ? "border-red-500" : ""
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-gray-500 text-sm font-medium">MXN</span>
              </div>
            </div>
            {getFieldError("maintenance_price") && (
              <p className="text-sm text-red-600 mt-1">
                {getFieldError("maintenance_price")}
              </p>
            )}
          </div>
        </div>

        <Separator className="my-6" />

        <AddressForm
          value={addressFormData}
          onChange={handleAddressChange}
          onBlur={handleAddressBlur}
          errors={addressErrors}
          touched={addressTouched}
          title="Dirección"
          transformValue={transformValue}
        />
      </div>
    );

    const renderLandlordSelection = () => {
      return (
        <LandlordSelector
          selectedLandlord={selectedLandlord}
          onSelectLandlord={setSelectedLandlord}
        />
      );
    };

    // Handle property registration
    const handleRegister = () => {
      validateAllFields();
      if (!validateStep1()) {
        return;
      }

      // Transform NewProperty to Property format for API
      const propertyData: CreatePropertyPayload = {
        alias: newProperty.alias ? removeAccents(newProperty.alias) : null,
        rental_price: newProperty.rental_price || null,
        maintenance_price: newProperty.maintenance_price || "0",
        usage: newProperty.uso || null,
        address: {
          street: newProperty.street ? removeAccents(newProperty.street) : null,
          exterior_number: newProperty.exterior_number
            ? removeAccents(newProperty.exterior_number)
            : null,
          interior_number: newProperty.interior_number
            ? removeAccents(newProperty.interior_number)
            : null,
          zip_code: newProperty.zip_code || null,
          neighborhood: newProperty.colonia
            ? removeAccents(newProperty.colonia)
            : null,
          municipality: newProperty.municipality
            ? removeAccents(newProperty.municipality)
            : null,
          city: newProperty.city ? removeAccents(newProperty.city) : null,
          state: newProperty.state ? removeAccents(newProperty.state) : null,
        },
        landlord_id:
          selectedLandlord && Object.keys(selectedLandlord).length > 0 && selectedLandlord.id
            ? selectedLandlord.id
            : null,
      };

      createPropertyMutation.mutate(propertyData, {
        onSuccess: (data) => {
          onPropertyCreated?.(data);
          resetForm();
          setPropertySheetStep(1);
          onCancelClick();
        },
        onError: (error: Error) => {
          console.error("Error creating property:", error);
        },
      });
    };

    return (
      <>
        <div>
          {createPropertyMutation.error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              {/*
                Este <p> es un HIJO DE REACT, no un toast: la coercion de
                utils/toast.ts no pasa por aqui. El cast a `{ message?: string }`
                mentia — el serializer responde
                `{code: 4000, message: {zip_code: ["..."]}}` y ese objeto
                tumbaba el arbol con "Objects are not valid as a React child"
                (sin ErrorBoundary intermedio: se cae la app entera).
              */}
              <p className="text-sm text-red-600">
                {getApiErrorMessage(
                  createPropertyMutation.error,
                  "Error al crear la propiedad. Inténtalo de nuevo.",
                )}
              </p>
            </div>
          )}
          <div className="min-h-[400px] pb-4">
            {propertySheetStep === 1 && renderPropertyForm()}
            {propertySheetStep === 2 && renderLandlordSelection()}
          </div>

          <div className="sticky bottom-0 left-0 right-0 bg-white z-20 -mx-6 px-6">
            <DialogFooter className="flex flex-row items-center pt-4 border-t border-gray-200">
              {propertySheetStep === 1 ? (
                <Button
                  variant="ghost"
                  onClick={handleCancelClick}
                  className="h-11 px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={handleBackClick}
                  className="h-11 px-6 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Regresar
                </Button>
              )}

              <div className="flex-1"></div>

              {propertySheetStep === 1 ? (
                <Button
                  onClick={() => {
                    validateAllFields();
                    if (!validateStep1()) return;
                    setPropertySheetStep(2);
                  }}
                  disabled={!validateStep1()}
                  className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleRegister}
                  disabled={createPropertyMutation.isPending}
                  className="bg-[#0f5ef7] hover:bg-[#0d52e0] text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                >
                  {createPropertyMutation.isPending ? (
                    <>Registrando...</>
                  ) : (
                    <>Registrar Inmueble</>
                  )}
                </Button>
              )}
            </DialogFooter>
          </div>
        </div>

        <CancelConfirmationDialog
          open={showCancelConfirmation}
          onOpenChange={setShowCancelConfirmation}
          onConfirm={handleConfirmCancel}
        />
      </>
    );
  },
);
